import type { ReactNode } from 'react';
import { ArrowRight, CalendarClock, FileText, ListChecks, Mail, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useFirstClaimSummary } from '@/hooks/use-first-claim-summary';
import { formatEnum } from '@/lib/enum-utils';
import {
  keyDatesFor,
  lossAddress,
  policyholderName,
  suggestNextSteps,
} from '@/lib/first-claim-next-steps';
import { formatShortDateOnly } from '@/lib/format-utils';

interface FirstClaimFoundProps {
  claimId: string;
  /** Total claims the scan created. More than one adds a link to the full list. */
  foundCount: number;
  accountEmail?: string | null;
  onOpenClaim: (claimId: string) => void;
  onViewAllClaims: () => void;
  onDismiss: () => void;
}

const MAX_LISTED = 3;

function Field({ label, value, testId }: { label: string; value: ReactNode; testId: string }) {
  return (
    <div className="min-w-0" data-testid={testId}>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words text-sm font-medium">
        {value || <span className="font-normal text-muted-foreground">Not found yet</span>}
      </dd>
    </div>
  );
}

function SectionTitle({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
      <span className="text-primary" aria-hidden="true">{icon}</span>
      {children}
    </h3>
  );
}

function FoundSkeleton() {
  return (
    <Card data-testid="first-claim-loading">
      <CardHeader>
        <Skeleton className="h-6 w-64" />
        <Skeleton className="h-4 w-80 max-w-full" />
      </CardHeader>
      <CardContent className="space-y-3">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </CardContent>
    </Card>
  );
}

export function FirstClaimFound({
  claimId,
  foundCount,
  accountEmail,
  onOpenClaim,
  onViewAllClaims,
  onDismiss,
}: FirstClaimFoundProps) {
  const summary = useFirstClaimSummary(claimId);

  if (summary.isLoading) return <FoundSkeleton />;

  if (summary.isError || !summary.claim) {
    return (
      <Card data-testid="first-claim-unreadable">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="h-5 w-5 text-primary" aria-hidden="true" />
            ClaimHive found {foundCount === 1 ? 'a claim' : `${foundCount} claims`} in your inbox
          </CardTitle>
          <CardDescription>We could not load the details just now. Open your claims to see what was pulled in.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={onViewAllClaims} data-testid="first-claim-view-claims">
            View claims
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { claim, emails, documents, pendingDeadlines } = summary;
  const keyDates = keyDatesFor(claim, pendingDeadlines);
  const nextSteps = suggestNextSteps(claim, documents, pendingDeadlines);
  const emailCount = emails.length;
  const documentCount = documents.length;

  return (
    <Card className="border-primary/40 shadow-sm" data-testid="first-claim-found">
      <CardHeader className="gap-3 sm:flex-row sm:items-start sm:justify-between sm:space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="flex items-center gap-2 text-xl">
            <Sparkles className="h-5 w-5 text-primary" aria-hidden="true" />
            {foundCount > 1 ? `ClaimHive found ${foundCount} claims in your inbox` : 'ClaimHive found your first claim'}
          </CardTitle>
          <CardDescription>
            {foundCount > 1 ? 'Here is the most recent one. ' : ''}
            Everything below came from {accountEmail ? accountEmail : 'your inbox'}, with no typing.
          </CardDescription>
        </div>
        <Button className="w-full shrink-0 sm:w-auto" onClick={() => onOpenClaim(claimId)} data-testid="first-claim-open">
          Open claim
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="space-y-3">
          <SectionTitle icon={<FileText className="h-4 w-4" />}>What ClaimHive pulled out</SectionTitle>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Policyholder" value={policyholderName(claim)} testId="found-policyholder" />
            <Field label="Carrier" value={claim.carrier?.name} testId="found-carrier" />
            <Field label="Claim number" value={claim.claimNumber} testId="found-claim-number" />
            <Field
              label="Date of loss"
              value={claim.dateOfLoss ? formatShortDateOnly(claim.dateOfLoss.slice(0, 10)) : null}
              testId="found-date-of-loss"
            />
            <Field label="Peril" value={claim.perilType ? formatEnum(claim.perilType) : null} testId="found-peril" />
            <Field label="Loss location" value={lossAddress(claim)} testId="found-location" />
          </dl>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <section data-testid="found-linked">
            <SectionTitle icon={<Mail className="h-4 w-4" />}>Linked from your inbox</SectionTitle>
            <div className="mb-2 flex flex-wrap gap-2">
              <Badge variant="secondary" data-testid="found-email-count">
                {emailCount} email{emailCount === 1 ? '' : 's'}
              </Badge>
              <Badge variant="secondary" data-testid="found-document-count">
                {documentCount} document{documentCount === 1 ? '' : 's'}
              </Badge>
            </div>
            {emailCount + documentCount === 0 ? (
              <p className="text-sm text-muted-foreground">Emails and attachments will appear here as they are linked.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {emails.slice(0, MAX_LISTED).map((email) => (
                  <li key={email.id} className="flex items-center gap-2 truncate">
                    <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                    <span className="truncate">{email.subject || '(No subject)'}</span>
                  </li>
                ))}
                {documents.slice(0, MAX_LISTED).map((doc) => (
                  <li key={doc.id} className="flex items-center gap-2 truncate">
                    <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                    <span className="truncate">{doc.displayName || doc.fileName}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section data-testid="found-key-dates">
            <SectionTitle icon={<CalendarClock className="h-4 w-4" />}>Key dates and deadlines</SectionTitle>
            {keyDates.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No deadlines found yet. ClaimHive adds them here as it reads the policy and carrier letters.
              </p>
            ) : (
              <ul className="space-y-1 text-sm">
                {keyDates.slice(0, 4).map((item) => (
                  <li key={item.id} className="flex items-baseline justify-between gap-3">
                    <span className="truncate">
                      {item.label}
                      {item.needsReview && <span className="ml-2 text-xs text-muted-foreground">to review</span>}
                    </span>
                    <span className="shrink-0 font-medium">{formatShortDateOnly(item.date)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <section data-testid="found-next-steps">
          <SectionTitle icon={<ListChecks className="h-4 w-4" />}>Suggested next steps</SectionTitle>
          <ol className="list-decimal space-y-1 pl-5 text-sm">
            {nextSteps.map((step) => (
              <li key={step.id}>{step.text}</li>
            ))}
          </ol>
        </section>

        <div className="flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
          <Button variant="ghost" size="sm" onClick={onDismiss} data-testid="first-claim-dismiss">
            Dismiss
          </Button>
          {foundCount > 1 && (
            <Button variant="outline" size="sm" onClick={onViewAllClaims} data-testid="first-claim-view-all">
              See all {foundCount} claims
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
